import { PostgrestError } from '@supabase/supabase-js'
import { supabase } from './supabase'
import type { BlockCode, Profile, ProfileStatus } from './profile'

/**
 * Mirrors `public.invites` (`supabase/migrations/0002_invites.sql`) column for column.
 */
export interface Invite {
  code: string
  created_by: string
  note: string | null
  max_uses: number
  uses: number
  expires_at: string | null
  revoked: boolean
  created_at: string
}

/**
 * Fetches every `profiles` row, for the admin screen's user list.
 *
 * No filter: `profiles_select_admin` (`0005_profiles_admin_read.sql`) is what makes this return
 * every row for an admin caller. A non-admin caller gets back only their own single row,
 * harmlessly — nothing but `AdminScreen` calls this, and `AppRoutes` already gates that screen on
 * `profile?.is_admin`, so a non-admin's RLS-narrowed result is never actually rendered anywhere.
 *
 * @returns A promise resolving to `{ profiles, error }`; `profiles` is `[]` on error or when the
 *          caller has no admin-read access.
 */
export async function fetchAllProfiles(): Promise<{
  profiles: Profile[]
  error: PostgrestError | null
}> {
  const { data, error } = await supabase.from('profiles').select('*')
  return { profiles: (data as Profile[] | null) ?? [], error }
}

/**
 * Fetches every `invites` row, for the admin screen's invite list.
 *
 * Relies on `invites_select_admin` (`0002_invites.sql`) the same way `fetchAllProfiles` relies on
 * `profiles_select_admin` — a non-admin caller reads zero rows, harmlessly.
 *
 * @returns A promise resolving to `{ invites, error }`; `invites` is `[]` on error or when the
 *          caller has no admin-read access.
 */
export async function fetchInvites(): Promise<{
  invites: Invite[]
  error: PostgrestError | null
}> {
  const { data, error } = await supabase.from('invites').select('*')
  return { invites: (data as Invite[] | null) ?? [], error }
}

/**
 * Mints a new invite code via `public.admin_create_invite(p_note, p_max_uses, p_expires_at)`
 * (`0003_audit_admin.sql`).
 *
 * Deliberately exposes only `note` — `max_uses` and `expires_at` are left at the database
 * function's own defaults (`1` and `null`) rather than becoming parameters here. The plan's
 * "minimal admin screen" doesn't ask for a fuller invite-configuration form; this is a scope
 * boundary, not an oversight.
 *
 * @param note - Optional free text so Oscar can tell invites apart later (e.g. "for Jane").
 * @returns A promise resolving to `{ code, error }`; `code` is the minted invite code on success.
 */
export async function adminCreateInvite(note: string | null): Promise<{
  code: string | null
  error: PostgrestError | null
}> {
  const { data, error } = await supabase.rpc('admin_create_invite', { p_note: note })
  return { code: error ? null : (data as string), error }
}

/**
 * Revokes an invite code via `public.admin_revoke_invite(p_code)` (`0003_audit_admin.sql`).
 *
 * `code` is passed through exactly as given — the database function already does
 * `upper(trim(p_code))` server-side, and this screen only ever calls it with a code it already
 * fetched (never hand-typed), so there's nothing to normalize client-side.
 *
 * @param code - The invite code to revoke, as fetched by `fetchInvites`.
 * @returns A promise resolving to `{ error }`; `error` is `null` on success.
 */
export async function adminRevokeInvite(code: string): Promise<{
  error: PostgrestError | null
}> {
  const { error } = await supabase.rpc('admin_revoke_invite', { p_code: code })
  return { error }
}

/**
 * Fixes a user's block via `public.admin_set_block(p_user_id, p_block_code)`
 * (`0003_audit_admin.sql`).
 *
 * The block-code shape check happens server-side (`errcode` `22023` on an invalid code); this
 * function passes the error through unchanged rather than re-validating client-side.
 *
 * @param userId - The `profiles.id` of the user whose block is being fixed.
 * @param blockCode - One of the eight block codes.
 * @returns A promise resolving to `{ error }`; `error` is `null` on success.
 */
export async function adminSetBlock(
  userId: string,
  blockCode: BlockCode,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase.rpc('admin_set_block', {
    p_user_id: userId,
    p_block_code: blockCode,
  })
  return { error }
}

/**
 * Disables or re-enables a user via `public.admin_set_status(p_user_id, p_status)`
 * (`0003_audit_admin.sql`).
 *
 * The status enum membership check happens server-side (`errcode` `22023` on an invalid status);
 * this function passes the error through unchanged rather than re-validating client-side. There is
 * no self-disable guard, by design (see `0003_audit_admin.sql`'s comment on `admin_set_status`) —
 * this function does not add one either.
 *
 * @param userId - The `profiles.id` of the user whose status is being changed.
 * @param status - The new status.
 * @returns A promise resolving to `{ error }`; `error` is `null` on success.
 */
export async function adminSetStatus(
  userId: string,
  status: ProfileStatus,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase.rpc('admin_set_status', {
    p_user_id: userId,
    p_status: status,
  })
  return { error }
}
